// Extra feature sections built from help center articles.
// Covers: payroll, client-management, intake-forms, data-transfer, invoices, payments.
export const MORE_OPERATIONS = {
    payroll: {
        tabLinks: {
            "one-click-push": "/help-center/knowledge-base/team/run-payroll-with-gusto#each-pay-period",
            "time-tracking": "/help-center/knowledge-base/team/manage-timesheets#review-timesheets",
            "tips-commissions": "/help-center/knowledge-base/team/view-team-earnings#view-earnings",
            "taxes-deposits": "/help-center/knowledge-base/team/run-payroll-with-gusto#connect-gusto",
        },
        sections: [
            {
                type: "row",
                title: "Time cards that fill themselves in",
                description: "Team members clock in, take breaks and clock out right from Zobliss. Every shift lands in timesheets, ready for your approval.",
                image: "team/time-clock-menu",
                href: "/help-center/knowledge-base/team/clock-in-and-out#use-the-time-clock",
                linkLabel: "Start tracking time",
            },
            {
                type: "group",
                heading: "Timesheets you can trust",
                intro: "Check every hour before pay day, without chasing anyone.",
                cards: [
                    {
                        title: "Approve shifts in a click",
                        description: "Open any shift to approve, flag, edit or delete it, or approve several team members at once.",
                        image: "team/timesheet-segment",
                        href: "/help-center/knowledge-base/team/manage-timesheets#review-timesheets",
                        linkLabel: "Review timesheets",
                    },
                    {
                        title: "Fix missed shifts, with a paper trail",
                        description: "Add a forgotten shift by hand. Every manual entry, edit and deletion needs a reason and is saved in the audit log.",
                        image: "team/timesheet-manual-entry",
                        href: "/help-center/knowledge-base/team/manage-timesheets#add-a-missed-shift",
                        linkLabel: "Add a missed shift",
                    },
                ],
            },
            {
                type: "row",
                title: "Every earning on one screen",
                description: "See each team member's hours, wages, time off, commission, tips and adjustments for any period, and print their report in a click.",
                image: "team/earnings",
                href: "/help-center/knowledge-base/team/view-team-earnings#view-earnings",
                linkLabel: "Make payroll make sense",
            },
            {
                type: "group",
                heading: "Commissions without the spreadsheet",
                intro: "Set the rules once and Zobliss works out every payout.",
                cards: [
                    {
                        title: "Rules for every team member",
                        description: "Pay a percentage or a fixed amount on services and products, and add overrides for specific items.",
                        image: "team/commission-rule",
                        href: "/help-center/knowledge-base/team/set-up-commissions#set-default-rules",
                        linkLabel: "Set commission rules",
                    },
                    {
                        title: "Tiers that reward growth",
                        description: "Tiered commission raises the percentage as a team member's sales grow, resetting each pay period or calendar month.",
                        image: "team/commission-tiered",
                        href: "/help-center/knowledge-base/team/set-up-commissions#set-default-rules",
                        linkLabel: "Add commission tiers",
                    },
                ],
            },
            {
                type: "row",
                title: "Bonuses and deductions in seconds",
                description: "Add a bonus or a deduction to any team member's earnings, with a reason, right from the earnings page.",
                image: "team/earnings-adjustment",
                href: "/help-center/knowledge-base/team/view-team-earnings#add-an-adjustment",
                linkLabel: "Add an adjustment",
            },
            {
                type: "row",
                title: "Tips split fairly",
                description: "When more than one team member works on a sale, split the tip between them at checkout so everyone gets their share.",
                image: "sales/sale-tip-split",
                href: "/help-center/knowledge-base/sales/set-up-and-manage-tips#split-a-tip",
                linkLabel: "Split a tip",
            },
            {
                type: "row",
                title: "Keep payouts private",
                description: "On a shared front desk device, a 4 digit PIN stops team members opening Payouts and Reports from the calendar without permission.",
                image: "team/front-desk-pin",
                href: "/help-center/knowledge-base/team/set-a-front-desk-pin#set-the-pin",
                linkLabel: "Set a front desk PIN",
            },
        ],
    },
    "client-management": {
        tabLinks: {
            "client-profiles": "/help-center/knowledge-base/clients/view-client-history#overview",
            "consultation-history": "/help-center/knowledge-base/clients/add-notes-to-client-profiles#add-a-note",
            "waivers-intake": "/help-center/knowledge-base/clients/client-forms-overview#where-forms-appear",
            "retention-notes": "/help-center/knowledge-base/reports/most-valuable-clients-report#what-it-shows",
        },
        sections: [
            {
                type: "row",
                title: "Every visit, one tap away",
                description: "See every appointment a client has had, with services, team members and prices. Rebook their next visit straight from their history.",
                image: "clients/client-appointments",
                href: "/help-center/knowledge-base/clients/view-client-history#appointments",
                linkLabel: "View client history",
            },
            {
                type: "group",
                heading: "Know what every client spends",
                cards: [
                    {
                        title: "Purchase history at a glance",
                        description: "Every sale, from appointment checkouts to quick sales, with totals, refunds and how it was paid.",
                        image: "clients/client-sales",
                        href: "/help-center/knowledge-base/clients/view-client-history#sales",
                        linkLabel: "See client sales",
                    },
                    {
                        title: "One wallet for everything",
                        description: "A client's rewards, gift card balance and saved cards live together in their wallet.",
                        image: "clients/client-wallet-balance",
                        href: "/help-center/knowledge-base/clients/manage-client-wallets#open-a-client-wallet",
                        linkLabel: "Open a client wallet",
                    },
                ],
            },
            {
                type: "row",
                title: "Paperwork that stays with the profile",
                description: "Keep signed consent forms, before photos and other documents on the client's profile. Files are private to your team.",
                image: "clients/client-files",
                href: "/help-center/knowledge-base/clients/add-files-to-client-profiles#upload-a-file",
                linkLabel: "Upload client files",
            },
            {
                type: "row",
                title: "Little treats, big loyalty",
                description: "Give a client an amount or percentage discount, or a free service or product, as a thank-you or a birthday gift. It waits in their wallet until checkout.",
                image: "clients/reward-choose-type",
                href: "/help-center/knowledge-base/clients/manage-client-rewards#add-a-reward",
                linkLabel: "Reward your regulars",
            },
            {
                type: "group",
                heading: "Find any client fast",
                cards: [
                    {
                        title: "Your client list, your way",
                        description: "Search by name, phone or email, sort the list and switch between cards and a table.",
                        image: "clients/clients-table",
                        href: "/help-center/knowledge-base/clients/manage-your-client-list#find-a-client",
                        linkLabel: "Browse your clients",
                    },
                    {
                        title: "New clients in seconds",
                        description: "Add a client from the list or while booking. Clients who book online get a profile automatically.",
                        image: "clients/client-add",
                        href: "/help-center/knowledge-base/clients/create-client-profiles#add-a-client-from-the-client-list",
                        linkLabel: "Add a client",
                    },
                ],
            },
            {
                type: "row",
                title: "Details your team can count on",
                description: "Update contact details and preferences any time, and skip Google review requests for a client who would rather not get them.",
                image: "clients/client-edit-form",
                href: "/help-center/knowledge-base/clients/update-client-details#edit-a-client",
                linkLabel: "Keep details current",
            },
            {
                type: "row",
                title: "A full record of every reward",
                description: "See when each reward was added, used, removed or expired, and who did it.",
                image: "clients/reward-activity",
                href: "/help-center/knowledge-base/clients/manage-client-rewards#rewards-activity",
                linkLabel: "Track reward activity",
            },
        ],
    },
    "intake-forms": {
        tabLinks: {
            "custom-forms": "/help-center/knowledge-base/clients/create-a-form-template#create-the-template",
            "auto-send": "/help-center/knowledge-base/clients/create-a-form-template#request-a-form-for-a-service",
            "client-history": "/help-center/knowledge-base/clients/review-form-submissions#find-submissions",
        },
        sections: [
            {
                type: "row",
                title: "Forms that fit how you work",
                description: "Choose who fills out each form, whether it's needed every visit or just once, and if a team member must review and sign it.",
                image: "clients/form-details",
                href: "/help-center/knowledge-base/clients/create-a-form-template#choose-the-settings",
                linkLabel: "Set up your form",
            },
            {
                type: "group",
                heading: "Ask exactly what you need",
                intro: "Build any intake form or waiver from ready-made fields.",
                cards: [
                    {
                        title: "Ten field types to pick from",
                        description: "Names, email, phone, dates, short and long answers, dropdowns, multiple choice, choose one and toggles.",
                        image: "clients/form-field-picker",
                        href: "/help-center/knowledge-base/clients/create-a-form-template#add-form-fields",
                        linkLabel: "Add form fields",
                    },
                    {
                        title: "Answers clients just tap",
                        description: "Give clients options to choose from, and make any question required.",
                        image: "clients/form-choose-one",
                        href: "/help-center/knowledge-base/clients/create-a-form-template#add-form-fields",
                        linkLabel: "Build your questions",
                    },
                ],
            },
            {
                type: "row",
                title: "See it before your clients do",
                description: "Preview the form exactly as it will be filled out, and add a custom confirmation message for after it's submitted.",
                image: "clients/form-preview",
                href: "/help-center/knowledge-base/clients/create-a-form-template#preview-and-enable",
                linkLabel: "Preview your form",
            },
            {
                type: "row",
                title: "Hand over a tablet, not a clipboard",
                description: "Show a QR code at the front desk and clients fill out the form on an iPad or phone.",
                image: "clients/form-qr-code",
                href: "/help-center/knowledge-base/clients/request-and-complete-forms#add-a-form-to-an-appointment",
                linkLabel: "Go paperless",
            },
            {
                type: "group",
                heading: "Request forms your way",
                cards: [
                    {
                        title: "Add a form to any appointment",
                        description: "Attach a form to a booked appointment, then fill it out, copy the link or send a request to the client.",
                        image: "clients/appointment-add-form",
                        href: "/help-center/knowledge-base/clients/request-and-complete-forms#add-a-form-to-an-appointment",
                        linkLabel: "Request a form",
                    },
                    {
                        title: "Send, open or share a public link",
                        description: "Start a submission from the forms list, or share a public link anywhere, like on your website.",
                        image: "clients/form-new-submission",
                        href: "/help-center/knowledge-base/clients/request-and-complete-forms#start-a-new-submission",
                        linkLabel: "Start a submission",
                    },
                ],
            },
            {
                type: "row",
                title: "Forms right on the appointment",
                description: "Every form linked to a booking shows up in the appointment's Intake Forms section, so your team knows what's done before the client sits down.",
                image: "clients/appointment-intake-forms",
                href: "/help-center/knowledge-base/clients/client-forms-overview#where-forms-appear",
                linkLabel: "See where forms appear",
            },
            {
                type: "row",
                title: "Reuse what already works",
                description: "Save, duplicate or delete templates from one menu, so a new form never starts from scratch.",
                image: "clients/form-template-menu",
                href: "/help-center/knowledge-base/clients/create-a-form-template#preview-and-enable",
                linkLabel: "Manage templates",
            },
        ],
    },
    "soap-notes": {
        tabLinks: {
            templates: "/help-center/knowledge-base/clients/set-up-soap-notes#create-a-soap-note-template",
            "on-the-appointment": "/help-center/knowledge-base/clients/write-soap-notes#write-and-save-the-note",
            "sign-and-lock": "/help-center/knowledge-base/clients/sign-and-lock-soap-notes#sign-a-note",
        },
        sections: [
            {
                type: "row",
                title: "Pick a template, the note fills itself in",
                description: "Choose a template when you create a note and its default note and images are added for you, ready to fill in for this visit.",
                image: "clients/soap-note-create",
                href: "/help-center/knowledge-base/clients/write-soap-notes#write-and-save-the-note",
                linkLabel: "Start from a template",
            },
            {
                type: "group",
                heading: "Add a note where you work",
                intro: "Write a SOAP note from the calendar or from the client's profile.",
                cards: [
                    {
                        title: "Right on the appointment",
                        description: "Open a booking in the calendar and add a SOAP note in one click. Walk-ins included.",
                        image: "clients/soap-appointment",
                        href: "/help-center/knowledge-base/clients/write-soap-notes#add-a-note-from-the-calendar",
                        linkLabel: "Add from the calendar",
                    },
                    {
                        title: "From the client's notes",
                        description: "Choose Add note > SOAP in the client's profile and pick the visit it's for.",
                        image: "clients/soap-add-note-menu",
                        href: "/help-center/knowledge-base/clients/write-soap-notes#add-a-note-from-the-client-profile",
                        linkLabel: "Add from the profile",
                    },
                ],
            },
            {
                type: "row",
                title: "Mark it up, right on the image",
                description: "Add a body chart or photo to any note, then circle problem areas and add labels in the colour you choose. Templates can include images, so every note starts with one.",
                image: "clients/soap-attachment-large",
                href: "/help-center/knowledge-base/clients/write-soap-notes#write-and-save-the-note",
                linkLabel: "Draw on images",
            },
            {
                type: "row",
                title: "Every visit, one clear history",
                description: "SOAP notes sit with the client's other notes, newest first, with the appointment, a preview and whether each one is signed.",
                image: "clients/soap-client-notes",
                href: "/help-center/knowledge-base/clients/write-soap-notes#view-a-client-s-soap-notes",
                linkLabel: "View client notes",
            },
            {
                type: "group",
                heading: "Signatures your way",
                cards: [
                    {
                        title: "Choose how your team signs",
                        description: "Require a signature on every note, and pick typed names, drawn signatures, either one or both.",
                        image: "clients/soap-note-settings",
                        href: "/help-center/knowledge-base/clients/set-up-soap-notes#choose-your-note-settings",
                        linkLabel: "Set up signatures",
                    },
                    {
                        title: "Sign in a few taps",
                        description: "Team members booked on the appointment sign the note, and their name and the date are saved with it.",
                        image: "clients/soap-note-sign",
                        href: "/help-center/knowledge-base/clients/sign-and-lock-soap-notes#sign-a-note",
                        linkLabel: "Sign a note",
                    },
                ],
            },
            {
                type: "row",
                title: "Lock notes once they're final",
                description: "Lock a finished note so it can no longer be edited, signed or deleted. Edit a signed note and it must be signed again.",
                image: "clients/soap-note-lock",
                href: "/help-center/knowledge-base/clients/sign-and-lock-soap-notes#lock-a-note",
                linkLabel: "Lock a note",
            },
        ],
    },
    "data-transfer": {
        tabLinks: {
            "client-import": "/help-center/knowledge-base/clients/import-your-client-list#import-clients",
            "service-import": "/help-center/knowledge-base/services/import-services#import-from-csv",
            "product-import": "/help-center/knowledge-base/inventory/import-products#import-from-csv",
        },
        sections: [
            {
                type: "row",
                title: "No duplicates, no mess",
                description: "Preview your file before importing. Rows matching an existing client's email are skipped or update that client, your choice.",
                image: "clients/clients-table",
                href: "/help-center/knowledge-base/clients/import-your-client-list#import-clients",
                linkLabel: "Import your clients",
            },
            {
                type: "group",
                heading: "Your catalog, moved in",
                cards: [
                    {
                        title: "Services ready to book",
                        description: "Each service keeps its name, price and duration, and can be offered at some or all of your locations.",
                        image: "services/services-list",
                        href: "/help-center/knowledge-base/services/create-services#add-a-service",
                        linkLabel: "Review your services",
                    },
                    {
                        title: "Stock counts per location",
                        description: "Zobliss keeps a stock count for each product at each location and lowers it automatically when it sells.",
                        image: "inventory/inventory-table",
                        href: "/help-center/knowledge-base/inventory/track-stock-levels#view-stock",
                        linkLabel: "Track your stock",
                    },
                ],
            },
            {
                type: "row",
                title: "Fine-tune prices and durations",
                description: "After the move, adjust any service's price, duration or online deposit in a few clicks.",
                image: "services/service-pricing-duration",
                href: "/help-center/knowledge-base/services/set-pricing-and-durations#pricing",
                linkLabel: "Adjust your pricing",
            },
            {
                type: "group",
                heading: "Set up the rest in minutes",
                cards: [
                    {
                        title: "Your business, your details",
                        description: "Add your business name, email and phone. They appear on your booking site and in messages to clients.",
                        image: "workspace-settings/business-details",
                        href: "/help-center/knowledge-base/workspace-settings/update-business-details#edit-business-details",
                        linkLabel: "Add business details",
                    },
                    {
                        title: "Bring your team along",
                        description: "Add each team member with their contact details, locations and how they're paid.",
                        image: "team/team-members-list",
                        href: "/help-center/knowledge-base/team/add-team-members#add-a-team-member",
                        linkLabel: "Add team members",
                    },
                ],
            },
            {
                type: "row",
                title: "Every location in one place",
                description: "Each location has its own address, opening hours, booking settings, taxes and tips.",
                image: "workspace-settings/locations-list",
                href: "/help-center/knowledge-base/workspace-settings/manage-locations#view-locations",
                linkLabel: "Set up locations",
            },
        ],
    },
    invoices: {
        tabLinks: {
            "quick-creation": "/help-center/knowledge-base/sales/create-and-send-invoices#create-an-invoice",
            "pay-links": "/help-center/knowledge-base/sales/create-and-send-invoices#send-an-invoice",
            "status-tracking": "/help-center/knowledge-base/sales/manage-invoices#invoice-statuses",
            "deposit-recovery": "/help-center/knowledge-base/payments/collect-deposits#from-the-calendar",
        },
        sections: [
            {
                type: "row",
                title: "Bill for anything, from anywhere",
                description: "Invoices are ideal for custom events, remote product sales or collecting a balance without storing card details.",
                image: "sales/invoices-empty",
                href: "/help-center/knowledge-base/sales/create-and-send-invoices#create-an-invoice",
                linkLabel: "Create an invoice",
            },
            {
                type: "group",
                heading: "Invoicing without the busywork",
                cards: [
                    {
                        title: "Every action in one menu",
                        description: "View, send, edit, print or delete an invoice right from the list.",
                        image: "sales/invoice-actions-menu",
                        href: "/help-center/knowledge-base/sales/manage-invoices#invoice-actions",
                        linkLabel: "Manage invoices",
                    },
                    {
                        title: "Invoice new clients too",
                        description: "Not in your client list yet? Add a new client while you create the invoice.",
                        image: "clients/client-add",
                        href: "/help-center/knowledge-base/sales/create-and-send-invoices#create-an-invoice",
                        linkLabel: "Bill a new client",
                    },
                ],
            },
            {
                type: "row",
                title: "Secure pay links, ready to go",
                description: "Connect Stripe or Square once. Every invoice you send then reaches the client by email or text with a secure link to pay.",
                image: "payments/stripe-connected",
                href: "/help-center/knowledge-base/sales/create-and-send-invoices#send-an-invoice",
                linkLabel: "Start getting paid",
            },
            {
                type: "group",
                heading: "Deposits that count toward the bill",
                intro: "Lock in the booking now and only charge what's left later.",
                cards: [
                    {
                        title: "Collect a deposit as you book",
                        description: "Charge a saved card, a new card or a gift card for the deposit right from the calendar.",
                        image: "payments/collect-deposit",
                        href: "/help-center/knowledge-base/payments/collect-deposits#from-the-calendar",
                        linkLabel: "Collect a deposit",
                    },
                    {
                        title: "Only the balance at checkout",
                        description: "Checkout shows the deposit already collected and charges only the remaining amount.",
                        image: "payments/checkout-deposit-collected",
                        href: "/help-center/knowledge-base/payments/collect-deposits#at-checkout",
                        linkLabel: "See how it works",
                    },
                ],
            },
            {
                type: "row",
                title: "Stock that keeps up with invoices",
                description: "When a client pays an invoice that includes a product, its stock goes down automatically.",
                image: "inventory/inventory-table",
                href: "/help-center/knowledge-base/inventory/track-stock-levels#how-stock-changes",
                linkLabel: "Track your stock",
            },
            {
                type: "row",
                title: "Watch payments roll in",
                description: "Check your Stripe payments, balance and payouts without leaving Zobliss.",
                image: "payments/stripe-payments-tab",
                href: "/help-center/knowledge-base/payments/manage-your-stripe-account#open-stripe-details",
                linkLabel: "View your payments",
            },
        ],
    },
    payments: {
        tabLinks: {
            "tap-to-pay": "/help-center/knowledge-base/payments/take-square-terminal-payments#charge-on-the-terminal",
            "deposits-cards": "/help-center/knowledge-base/payments/collect-deposits#from-the-calendar",
            "payout-speed": "/help-center/knowledge-base/payments/manage-your-stripe-account#open-stripe-details",
            "safer-checkout": "/help-center/knowledge-base/payments/take-card-payments#card-options",
        },
        sections: [
            {
                type: "row",
                title: "Stripe, Square, or both",
                description: "Connect the processor you prefer, or both. Card payments go straight into your own Stripe or Square account.",
                image: "payments/client-billing",
                href: "/help-center/knowledge-base/payments/how-payments-work#stripe-or-square",
                linkLabel: "Choose your processor",
            },
            {
                type: "group",
                heading: "Checkout that moves fast",
                cards: [
                    {
                        title: "Charge a saved card in seconds",
                        description: "Pick the client's saved card at checkout, and the sale shows which processor took the payment.",
                        image: "payments/select-saved-card",
                        href: "/help-center/knowledge-base/payments/take-card-payments#charge-a-saved-card",
                        linkLabel: "Charge a saved card",
                    },
                    {
                        title: "Pair a Square Terminal in minutes",
                        description: "Enter a pairing code on the Terminal and checkout totals are sent straight to the device.",
                        image: "payments/square-pair-code",
                        href: "/help-center/knowledge-base/payments/pair-a-square-terminal#pair-a-device",
                        linkLabel: "Pair your Terminal",
                    },
                ],
            },
            {
                type: "row",
                title: "Cards saved safely",
                description: "Save a client's card to charge it later. Card details are stored securely by Stripe or Square, never by Zobliss.",
                image: "payments/add-stripe-card",
                href: "/help-center/knowledge-base/payments/save-client-cards#add-a-card",
                linkLabel: "Save a client card",
            },
            {
                type: "row",
                title: "Deposits paid at booking",
                description: "Clients see what's due today when they book online and pay with Stripe, Square or a gift card.",
                image: "payments/online-deposit-payment",
                href: "/help-center/knowledge-base/payments/collect-deposits#online-bookings",
                linkLabel: "Require a deposit",
            },
            {
                type: "group",
                heading: "Protect your time",
                intro: "Fewer no-shows and late cancellations, without awkward conversations.",
                cards: [
                    {
                        title: "A card on file, charged only when needed",
                        description: "Ask all clients, or just new ones, to add a card when they book online. Nothing is charged unless a deposit or fee applies.",
                        image: "payments/require-card-on-file",
                        href: "/help-center/knowledge-base/payments/require-a-card-on-file#turn-it-on",
                        linkLabel: "Require a card",
                    },
                    {
                        title: "Late cancellations covered",
                        description: "Cancellation fees come from the deposit first, then gift cards, then the saved card. Anything left is refunded.",
                        image: "payments/cancel-deposit-refund",
                        href: "/help-center/knowledge-base/payments/charge-cancellation-fees#how-the-fee-is-paid",
                        linkLabel: "Charge cancellation fees",
                    },
                ],
            },
            {
                type: "row",
                title: "Refunds back to the original card",
                description: "Refund all or part of a card payment through the processor that took it, with a clear breakdown of where the money goes.",
                image: "payments/refund-to-stripe",
                href: "/help-center/knowledge-base/payments/refund-card-payments#issue-a-refund",
                linkLabel: "Issue a refund",
            },
            {
                type: "row",
                title: "Your Stripe activity, inside Zobliss",
                description: "See your Stripe payments, balance and payouts without logging in anywhere else.",
                image: "payments/stripe-payments-tab",
                href: "/help-center/knowledge-base/payments/manage-your-stripe-account#open-stripe-details",
                linkLabel: "Check your payouts",
            },
        ],
    },
};
